// ─── Input validation & sanitization ─────────────────────────────────────────
// Defense-in-depth layer on top of Supabase's parameterized queries.
// Purpose: reject malformed/malicious input before it reaches the database.

// ── Injection detection ───────────────────────────────────────────────────────

// SQL injection patterns: keywords + quote combos that signal an attack
const SQL_INJECTION = /('|")\s*(or|and)\s*('|")\d+=\d+|';\s*(drop|delete|truncate|insert|update|alter|create|exec)\b|--\s*$|\/\*[\s\S]*?\*\/|\bunion\b.+\bselect\b|\bselect\b.+\bfrom\b/i;

// HTML/script injection
const SCRIPT_INJECTION = /<\s*script|javascript\s*:|vbscript\s*:|on\w+\s*=|<\s*iframe|<\s*img[^>]+onerror|eval\s*\(|expression\s*\(/i;

// Control characters (null byte, backspace, etc.)
const CONTROL_CHARS = /[\x00-\x08\x0b\x0c\x0e-\x1f\x7f]/;

export function detectInjection(value: string): boolean {
  return SQL_INJECTION.test(value) || SCRIPT_INJECTION.test(value) || CONTROL_CHARS.test(value);
}

// ── Text sanitization ─────────────────────────────────────────────────────────

export function sanitizeText(value: string, maxLength = 500): string {
  return value
    .replace(/[\x00-\x08\x0b\x0c\x0e-\x1f\x7f]/g, '') // strip control chars (keep \t \n \r)
    .slice(0, maxLength);
}

// ── Field validators (return error string or null) ────────────────────────────

export interface ValidateNameOptions {
  label?: string;
  min?: number;
  max?: number;
  /** If true, allows only letters, numbers, spaces and common punctuation */
  strict?: boolean;
}

export function validateName(
  value: string,
  { label = 'Nome', min = 2, max = 100, strict = true }: ValidateNameOptions = {}
): string | null {
  const v = value.trim();
  if (v.length < min) return `${label} deve ter pelo menos ${min} caracteres.`;
  if (v.length > max) return `${label} pode ter no máximo ${max} caracteres.`;
  if (detectInjection(v)) return `${label} contém padrões inválidos ou suspeitos.`;
  if (strict) {
    // Allow: letters (including accented), numbers, spaces, - . , ' " ( ) / & @
    const allowed = /^[\p{L}\p{N}\s\-.,'"()/&@!?:_+#%]+$/u;
    if (!allowed.test(v)) return `${label} contém caracteres não permitidos.`;
  }
  return null;
}

export function validateDescription(value: string, max = 2000): string | null {
  if (value.length > max) return `Descrição pode ter no máximo ${max} caracteres.`;
  if (detectInjection(value)) return 'Descrição contém padrões inválidos ou suspeitos.';
  return null;
}

export function validateSKU(value: string): string | null {
  if (!value.trim()) return null; // optional
  if (value.length > 50) return 'SKU pode ter no máximo 50 caracteres.';
  if (!/^[A-Za-z0-9\-_.]+$/.test(value.trim())) return 'SKU deve conter apenas letras, números, hífens, underscores e pontos.';
  return null;
}

export function validatePositiveInt(value: number, label: string, max = 9_999_999): string | null {
  if (!Number.isInteger(value) || value < 0) return `${label} deve ser um número inteiro positivo.`;
  if (value > max) return `${label} não pode ser maior que ${max.toLocaleString('pt-BR')}.`;
  return null;
}

export function validatePrice(cents: number, label = 'Preço'): string | null {
  if (isNaN(cents) || cents < 0) return `${label} não pode ser negativo.`;
  if (cents > 999_999_999) return `${label} excede o limite permitido.`;
  return null;
}

export function validateEmail(value: string): string | null {
  if (!value.trim()) return null; // optional
  if (value.length > 254) return 'E-mail muito longo (máx. 254 caracteres).';
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  if (!re.test(value.trim())) return 'E-mail inválido.';
  if (detectInjection(value)) return 'E-mail contém padrões inválidos.';
  return null;
}

export function validatePhone(value: string): string | null {
  if (!value.trim()) return null; // optional
  const digits = value.replace(/\D/g, '');
  if (digits.length < 10 || digits.length > 11) return 'Telefone deve ter 10 ou 11 dígitos (com DDD).';
  return null;
}

// ── Chat: bloqueio de telefone / CEP ──────────────────────────────────────────
// Mantém o contato dentro da plataforma. Detecta sequências que parecem
// telefone (10–11 dígitos) ou CEP (8 dígitos) em qualquer formato — inclusive
// com máscara, com DDI (+55) ou com os dígitos separados por espaços.
export function detectContactInfo(value: string): boolean {
  // Runs de 8 ou mais dígitos permitindo separadores comuns entre eles:
  // espaço, ponto, hífen, parênteses, barra. Ex.: "12345-678", "(11) 91234-5678",
  // "11 91234 5678", "1 1 9 1 2 3 4 5 6 7 8".
  const digitRun = /(?:\d[\s.\-()/]*){8,}/;
  if (digitRun.test(value)) return true;
  // DDI brasileiro seguido de dígitos
  if (/\+\s*55[\s.\-()]*\d/.test(value)) return true;
  return false;
}

// ── CPF / CNPJ ────────────────────────────────────────────────────────────────

export function validateCPF(cpf: string): boolean {
  const d = cpf.replace(/\D/g, '');
  if (d.length !== 11 || /^(\d)\1{10}$/.test(d)) return false;
  const mod = (sum: number) => { const r = (sum * 10) % 11; return r >= 10 ? 0 : r; };
  const s1 = d.split('').slice(0, 9).reduce((acc, n, i) => acc + Number(n) * (10 - i), 0);
  if (mod(s1) !== Number(d[9] ?? '')) return false;
  const s2 = d.split('').slice(0, 10).reduce((acc, n, i) => acc + Number(n) * (11 - i), 0);
  return mod(s2) === Number(d[10] ?? '');
}

export function validateCNPJ(cnpj: string): boolean {
  const d = cnpj.replace(/\D/g, '');
  if (d.length !== 14 || /^(\d)\1{13}$/.test(d)) return false;
  const calc = (s: string, w: number[]) => {
    const rem = s.split('').slice(0, w.length).reduce((a, n, i) => a + Number(n) * (w[i] ?? 0), 0) % 11;
    return rem < 2 ? 0 : 11 - rem;
  };
  return (
    calc(d, [5,4,3,2,9,8,7,6,5,4,3,2]) === Number(d[12] ?? '') &&
    calc(d, [6,5,4,3,2,9,8,7,6,5,4,3,2]) === Number(d[13] ?? '')
  );
}

/** Máscara progressiva de CPF (000.000.000-00) ou CNPJ (00.000.000/0000-00) */
export function maskCpfCnpj(value: string): string {
  const d = value.replace(/\D/g, '').slice(0, 14);
  if (d.length <= 11) {
    return d
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  }
  return d
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1/$2')
    .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
}

export function validateDocument(value: string): string | null {
  if (!value.trim()) return null; // optional
  const digits = value.replace(/\D/g, '');
  if (digits.length === 11) return validateCPF(value) ? null : 'CPF inválido (dígitos verificadores incorretos).';
  if (digits.length === 14) return validateCNPJ(value) ? null : 'CNPJ inválido (dígitos verificadores incorretos).';
  return 'Informe um CPF (11 dígitos) ou CNPJ (14 dígitos) válido.';
}

// ── Convenience: run multiple validators, return first error ──────────────────

export function firstError(...results: (string | null)[]): string | null {
  return results.find((r) => r !== null) ?? null;
}
